import React, { useState, useRef, useEffect } from "react";
import { convertHTMLRawStringToHTML, RCCButton, RCCGrid, RCCHookToast, RCCServicesApi, RCCTextArea, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import SortableHandle from "src/components/Sortable/Handle";
import SortableItem from "src/components/Sortable/Item";
import { useTranslation } from "src/context/Translations/context";
import ListOfActions, { IRefListOfActions } from "src/pages/Actions/Panel/listOfActions";
import IFiltersActionsPanel, { ActionGroupers, IRefActionsPanelModalAddChangeDelete } from "src/pages/Actions/Panel/interface";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import stringToNumber from "src/utils/stringToNumber";
import ModalAddChangeDelete from "src/pages/Actions/Panel/components/modalAddChangeDelete";
import { convertIFileDTOArrayToFileArray } from "src/Interfaces/fileDTO";
import { PermissionEnum } from "src/context/Auth/interface";
import { useHistory } from "react-router-dom";
import { useAuth } from "src/context/Auth/context";
import Accordion from "../Accordion";
import { EnumTablesRelated, IComponentFCAItem, IInternalFCA } from "./interface";
import Tabs from "../Tabs";
import FiveWhy from "./5Why";
import addOrChangeSchema from "./schemas";
import FadedDiv from "../Animations/fade";
import Attachments from "../Attachments";
import FishBoneChart from "./FishBoneChart";
import Title from "../Title";
import StyledTitleFCA from "./styles";
import { IRefFishBoneChart } from "./FishBoneChart/interface";
import CheckPermission from "../CheckPermission";

const styleFlex = { display: "flex", alignItems: "center"};
const styleRed = { color: "var(--red)", marginLeft: "4px", fontSize: "0.8rem" };

const FCAItem: React.FC<IComponentFCAItem> = ({
	defaultValue,
	index,
	delete: deleteFunction,
	disabled,
	isPresentation,
	tablesRelated,
	id,
	id2,
	updateFCAs
}) => {
	
	const { checkIfHasSpecificPermission } = useAuth();
	const history = useHistory();
	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
	const { register, control, handleSubmit, setValue, watch, unregister, clearErrors, setError, formState: { errors },getValues } = useForm({ shouldUnregister: true });
	
	const [loadedAllActions, setLoadedAllActions] = useState(!!defaultValue.actions);
	const [showCriticalAnalysis, setShowCriticalAnalysis] = useState<boolean>(defaultValue?.intba003?.length > 0 || defaultValue?.intba005?.length > 0 );
	const [showAttachments, setShowAttachments] = useState<boolean>(defaultValue.showAttachments||false);
	const [internalExpanded, setInternalExpanded] = useState(defaultValue.expanded || false);
	const [attachments, setAttachments] = useState<File[] | undefined>(defaultValue.attachments);
	const [initialTab,setInitialTab] = useState<number>(defaultValue.initialTab||defaultValue.intba003?.length === 0 && defaultValue.intba005?.length > 0 ? 1 : 0);
	
	const listOfActionsRef = useRef<IRefListOfActions>(null);
	const modalsRef = useRef<IRefActionsPanelModalAddChangeDelete>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const fishBoneRef = useRef<IRefFishBoneChart>(null);
	
	const executeLoadActions = () => {
		listOfActionsRef.current?.executeQuery();
		setLoadedAllActions(defaultValue.ina002Id > 0);
	};

	const onSubmit = async(data: any) => {

		async function executeFetch(dataForm: any){
			let newDefaultValue = {} as IInternalFCA;
			const formData = new FormData();
			formData.append("Obj", JSON.stringify({
				...dataForm,
				tablesRelated,
				idRelated: id,
				id2Related: id2,
				order: index
			}));
			let editAttachments = false;
			if(dataForm.attachments){
				dataForm.attachments.forEach((attach: any, idx: number) => {
					formData.append(`Attachments[${idx}]`, attach);
				});
				editAttachments = true;
			}
			formData.append("EditAttachments", String(editAttachments));
			if(dataForm.ina002Id > 0){
				const response = await RCCServicesApi.putRequest({
					method: "FCA/Update",
					contentType: "multipart/form-data",
					body: formData
				});
				newDefaultValue = response.data;
			}else{
				const response = await RCCServicesApi.postRequest({
					method: "FCA/Insert",
					contentType: "multipart/form-data",
					body: formData
				});
				newDefaultValue = response.data;
			}
			updateFCAs(index, newDefaultValue, editAttachments, editAttachments ? dataForm.attachments.length : defaultValue.attachmentsNumber);
			unregister();
		}

		try{
			setLoading(true);
			clearErrors();
			
			const dataForm = {
				...data,
				ina002Id: stringToNumber(data.ina002Id),
				intba003: data.intba003 && data.intba003.length > 0 ? data.intba003.map((five: any) => ({
					...five,
					ina003Id: stringToNumber(five.ina003Id)
				})) : [],
				intba005:fishBoneRef.current?.getValues()
			};
			
			await addOrChangeSchema(translate).validate(dataForm, {
				abortEarly: false
			});
			
			await executeFetch(dataForm);

			addToast({
				color: "green",
				description: translate("Valores da FCA inseridos/alterados com sucesso"),
				title: translate("Sucesso")
			});
			
			setLoading(false);
		}catch(err){
			setLoading(false);
			if(getValidationErrors(err, setError)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}
			
			standardErrorMessages(err, translate("Erro ao inserir/alterar FCA"), addToast);
		}
	};

	const updateState = () => {
		const newValues = getValues();
		newValues.intba005=fishBoneRef.current?.getValues();
		newValues.initialTab=initialTab;
		newValues.showAttachments=showAttachments;
		newValues.actions = listOfActionsRef.current?.getActions();
		const values = {...defaultValue,...newValues};
		updateFCAs(index, values as IInternalFCA, true, values.attachments ? values.attachments.length : defaultValue.attachmentsNumber);
	};

	const fetchAttachments = async () => {
		setShowAttachments(false);
		try{
			const response = await RCCServicesApi.getRequest({
				method: "FCA/SelectAttachments",
				urlParameters: {
					id: defaultValue.ina002Id
				}
			});
			let dataFormatted: File[] = [];

			if(response.data && response.data.length > 0)
				dataFormatted = convertIFileDTOArrayToFileArray(response.data);

			setAttachments(dataFormatted);
			setShowAttachments(true);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar anexos"), addToast);
		}
	};

	const handleClickExpand = (expanded: boolean) => {
		setInternalExpanded(expanded);
	};

	useEffect(() => {
		if(!listOfActionsRef.current || loadedAllActions || !internalExpanded || defaultValue.actions)
			return;

		executeLoadActions();
	}, [internalExpanded]);


	useEffect(() => {
		if(!attachments && isPresentation)
			fetchAttachments();
	}, []);


	return(
		<SortableItem id={defaultValue.id}>
			<Accordion 
				header={
					<RCCGrid 
						container
						item 
						alignItems="center"
						justifyContent="space-between"
					>
						<StyledTitleFCA>
							{index + 1}. FCA: {defaultValue.ina002Fato}
						</StyledTitleFCA>
						{!isPresentation && (
							<div style={styleFlex}>
								<RCCTooltip
									color={showCriticalAnalysis ? "blue" : "grey"}
									text={`${showCriticalAnalysis ? translate("Ocultar") : translate("Mostrar")} ${translate("Análises críticas")}`}
								>
									<RCCButton
										color={showCriticalAnalysis ? "blue" : "grey"}
										outlined
										size="xs"
										icon={{
											name: "eye"
										}}
										layout="isRounded"
										onClick={(e) => {
											e.stopPropagation();
											setShowCriticalAnalysis((prevState) => !prevState);
										}}
										disabled={disabled}
										tabIndex={-1}
									/>
								</RCCTooltip>
								<RCCTooltip
									color={showAttachments ? "blue" : "grey"}
									text={translate("Anexos")}
								>
									<RCCButton
										color={showAttachments ? "blue" : "grey"}
										outlined
										size="xs"
										icon={{
											name: "attachment"
										}}
										layout="isRounded"
										onClick={async (e) => {
											e.stopPropagation();
											if(!attachments){
												await fetchAttachments();
											}
										}}
										disabled={disabled}
										tabIndex={-1}
										styleInline="span{ color: var(--grey); }"
									>{defaultValue.attachmentsNumber > 0 && defaultValue.attachmentsNumber}</RCCButton>
								</RCCTooltip>
								{defaultValue.ina002Id > 0 && (
									<SortableHandle>
										<RCCTooltip
											color="grey"
											text={translate("Ordenar")}
										>
											<RCCButton
												color="grey"
												outlined
												size="xs"
												icon={{
													name: "arrows"
												}}
												layout="isRounded"
												disabled={disabled}
												onClick={(e) => e.stopPropagation()}
												tabIndex={-1}
											/>
										</RCCTooltip>	
									</SortableHandle>
								)}
								<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
									<RCCTooltip
										color="red"
										text={translate("Excluir")}
									>
										<RCCButton
											color="red"
											outlined
											size="xs"
											icon={{
												name: "trash"
											}}
											layout="isRounded"
											onClick={(e) => {
												e.stopPropagation();
												deleteFunction(defaultValue.ina002Id, index);
											}}
											disabled={disabled}
											tabIndex={-1}
										/>
									</RCCTooltip>
								</CheckPermission>
							</div>
						)}		
					</RCCGrid>
				}
				expanded={internalExpanded}
				unmountOnExit={false}
				onChange={(_, expanded) => handleClickExpand(expanded)}
			>		
				<div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }} >
					<input 
						type="text"
						value={String(defaultValue.ina002Id)}
						{...register("ina002Id")} 
						style={{display: "none"}}
						readOnly
					/>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
						md={8}
					>
						{!isPresentation ? (
							<>
								<Title size="md" color="purple">
									{translate("Fato")}
									<span style={styleRed}>*</span>		
								</Title>
								<RCCTextArea 
									name="ina002Fato"
									label={" "}
									maxLength={4000}
									register={register}
									defaultValue={defaultValue.ina002Fato}
									error={errors.ina002Fato}
									onBlurCapture={updateState}
									disabled={disabled}
									styleInline="padding: 0; >span.text-count{ top: 1px; }"
								/>
							</>
						
						) : (
							<RCCGrid>
								<Title size="lg" color="purple" bold>
									{`${translate("Fato")}:`}
								</Title>
								<Title size="lg" color="defaultColor">
									<RCCGrid dangerouslySetInnerHTML={{__html: `<span>${convertHTMLRawStringToHTML(defaultValue.ina002Fato).html}</span>` }} />
								</Title>
							</RCCGrid>
						)}
					</RCCGrid>	
					<div ref={containerRef} style={{display: "flex", width: "100%" }}>
						<div style={showCriticalAnalysis ? {display: "flex", width: "100%" } : { display: "none" }}>
							<Tabs 
								name="TabsFCA"
								tabInit={initialTab}
								options={[{
									label: translate("Cinco porquês"),
									onClick:() => setInitialTab(0),
									content: <FiveWhy 
										register={register}
										control={control}
										addResponseToCause={(val: string) => {
											const prevValue = watch("ina002Causa");
											setValue("ina002Causa", `${prevValue} ${val}`);
										}}
										clearErrors={clearErrors}
										fiveWhyDefaultValue={defaultValue.intba003}
										watch={watch}
										disabled={disabled}
										isPresentation={isPresentation}
										errors={errors}
										updateFunction={updateState}
									/>
								}, {
									label: translate("Diagrama ishikawa"),
									onClick:() => setInitialTab(1),
									content: 
									<div >
										<FishBoneChart 
											ref={fishBoneRef}
											parentContainerRef={containerRef}
											fcaId={defaultValue.ina002Id}
											fishBoneDefaultData={defaultValue.intba005}
											disabled={disabled || isPresentation}
											updateFunction={updateState}
											canDelete={checkIfHasSpecificPermission(history.location.pathname, PermissionEnum.CREATE_UPDATE_DELETE)}
										/>
									</div>
								}]}
								styleInline="width: 100%;"
							/>
						</div>
					</div>
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
						md={8}
					>
						{!isPresentation ? (
							<>
								<Title size="md" color="purple">
									{translate("Causa")}
									<span style={styleRed}>*</span>		
								</Title>
								<RCCTextArea 
									name="ina002Causa"
									label={" "}
									maxLength={4000}
									register={register}
									defaultValue={defaultValue.ina002Causa}
									error={errors.ina002Causa}
									onBlurCapture={updateState}
									disabled={disabled}
									styleInline="padding: 0; >span.text-count{ top: 1px; }"
								/>
							</>
						
						) : (
							<RCCGrid>
								<Title size="lg" color="purple" bold>
									{`${translate("Causa")}:`}
								</Title>
								<Title size="lg" color="defaultColor">
									<RCCGrid dangerouslySetInnerHTML={{__html: `<span>${convertHTMLRawStringToHTML(defaultValue.ina002Causa).html}</span>` }} />
								</Title>
							</RCCGrid>
						)}
					
					</RCCGrid>

					<br />
					
					<RCCGrid
						container
						item
						xs={12}
						sm={10}
						md={8}
					>
						{showAttachments && (
							<FadedDiv alignItems="center">
								<Attachments 
									name="attachments"
									filesInit={attachments}
									disabled={disabled || isPresentation}
									setValue={(name:string,values:any) => {
										setValue(name,values);
										if(values === defaultValue.attachments) return;
										updateState();
									}}
									styleInline="width: 100%;"
									
								/>
							</FadedDiv>
						)}
					</RCCGrid>
					{!isPresentation && (
						<RCCGrid
							container
							item
							alignItems="center"
							justifyContent="center"
						>
							<RCCTooltip
								color="green"
								text={translate("Salvar")}
							>
								<RCCButton 
									color="green"
									icon={{
										name: "save"
									}}
									onMouseDown={(e) => {
										e.stopPropagation();
										handleSubmit(onSubmit)(e);
									}}
									disabled={disabled}
									styleInline="padding: 0.5rem 4rem; @media(max-width: 600px){ padding: 0.25rem 2rem; }"
								>{translate("Salvar")}</RCCButton>
							</RCCTooltip>
						</RCCGrid>
					)}
					<br />	
					{defaultValue.ina002Id > 0 && (
						<ListOfActions 
							ref={listOfActionsRef}
							filters={{} as IFiltersActionsPanel}
							groupersSelect={{
								group: tablesRelated === EnumTablesRelated.INDICATORS ? ActionGroupers.FCA : ActionGroupers.FACT_CAUSE_OCCURENCE,
								delayed: "",
								origin: ""
							}}
							groupers={tablesRelated === EnumTablesRelated.INDICATORS ? {
								grouperId: id2 || 0,
								grouperId2: id,
								grouperId3: defaultValue.ina002Id
							} : {
								grouperId: defaultValue.ina002Id
							}}
							layoutList="card"
							defaultActions={defaultValue.actions}
							onCallbackAdd={updateState}
							header={{
								showTotal: true,
								button: isPresentation ? <></> : <RCCTooltip
									text={translate("Nova ação")}
									color="purple"
									position="leftEnd"
								>
									<RCCButton
										icon={{name: "plus", color: "#FFF", size: 18}}
										color="purple"
										layout="isRounded"
										onClick={() => { if(!modalsRef.current) return; modalsRef.current.openModalAddOrChange(undefined, () => listOfActionsRef.current?.executeQuery(), undefined, undefined, {factAndCause: defaultValue.ina002Id}); }}
										size="md"
										disabled={disabled}
									/>
								</RCCTooltip>
							}}
						/>
					)}				
					
				</div>

				{/* Modal ADD actions inside fca */}
				<ModalAddChangeDelete ref={modalsRef} />

			</Accordion>										
		</SortableItem>
	);};

export default FCAItem;
